import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import useAuth from '../../hooks/useAuth';

// ─── Constants ────────────────────────────────────────────────────────────────

const APPROVER_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner / Sir' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior_therapist', label: 'Senior Therapist' },
];

const STATUS_META = {
  draft:     { label: 'Draft',     color: '#6b7280', bg: '#f3f4f6' },
  active:    { label: 'Active',    color: '#2563eb', bg: '#eff6ff' },
  completed: { label: 'Completed', color: '#16a34a', bg: '#dcfce7' },
  paused:    { label: 'Paused',    color: '#d97706', bg: '#fef3c7' },
};

const STEP_STATUS_META = {
  pending:    { label: 'Pending',     color: '#6b7280', bg: '#f3f4f6', icon: '⏳' },
  in_progress:{ label: 'In Progress', color: '#2563eb', bg: '#dbeafe', icon: '🔄' },
  completed:  { label: 'Completed',   color: '#16a34a', bg: '#dcfce7', icon: '✅' },
  approved:   { label: 'Approved',    color: '#059669', bg: '#d1fae5', icon: '✔️' },
  blocked:    { label: 'Blocked',     color: '#dc2626', bg: '#fee2e2', icon: '🚫' },
  skipped:    { label: 'Skipped',     color: '#9ca3af', bg: '#f9fafb', icon: '⏭️' },
};

// ─── Built-in Templates ────────────────────────────────────────────────────────

const FLOW_TEMPLATES = [
  {
    id: 'order_to_dispatch',
    name: 'Order to Dispatch',
    description: 'End-to-end workflow from order receipt to customer delivery notification.',
    icon: '📦',
    color: '#f59e0b',
    bg: '#fffbeb',
    steps: [
      {
        title: 'Receive & Review Order',
        what: 'Review order details, confirm items, quantities, delivery address, and payment status.',
        how: { type: 'checklist', items: ['Check order items & quantities', 'Verify payment received', 'Confirm delivery address', 'Acknowledge order to customer'] },
        whenType: 'immediately', whenOffset: 0, whenUnit: 'hrs',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Inventory Check',
        what: 'Check stock availability for all ordered items. Flag out-of-stock items immediately.',
        how: { type: 'checklist', items: ['Verify warehouse stock', 'Confirm item condition', 'Flag any shortages to manager', 'Update inventory system'] },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'hrs',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Pick & Pack',
        what: 'Pick items from warehouse, pack securely with appropriate packaging material and labelling.',
        how: { type: 'sop', sopText: '1. Use bubble wrap for fragile items.\n2. Label package with Order ID clearly.\n3. Include packing slip inside the package.\n4. Weigh and record package dimensions.' },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'hrs',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Quality Check',
        what: 'Verify packed items match the order. Check packaging integrity before dispatch.',
        how: { type: 'checklist', items: ['Items match order slip', 'Packaging is secure & undamaged', 'Label is correct', 'Packing slip included', 'Weight is correct'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'hrs',
        requiresApproval: true, approvers: ['manager'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Dispatch to Courier',
        what: 'Hand over package to courier partner. Collect tracking number and update order status.',
        how: { type: 'sop', sopText: '1. Hand over to courier or drop at hub.\n2. Collect AWB/tracking number.\n3. Update order management system with tracking.\n4. File courier receipt.' },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'hrs',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Customer Notification',
        what: 'Notify customer of dispatch with tracking link and expected delivery date.',
        how: { type: 'sop', sopText: '1. Send WhatsApp message with tracking number.\n2. Send confirmation email with tracking link.\n3. Update CRM with dispatched status.\n4. Log dispatch date for follow-up.' },
        whenType: 'immediately', whenOffset: 0, whenUnit: 'hrs',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: true,
      },
    ],
  },

  {
    id: 'annual_event',
    name: 'Annual Event — 100 People',
    description: 'Complete planning and execution workflow for a large-scale annual gathering or celebration.',
    icon: '🎉',
    color: '#8b5cf6',
    bg: '#f5f3ff',
    steps: [
      {
        title: 'Concept & Date Finalization',
        what: 'Define event theme, objectives, target date, and guest count. Get initial approval from management.',
        how: { type: 'checklist', items: ['Define event theme & objectives', 'Fix date & time', 'Estimate guest count (target 100)', 'Set preliminary budget', 'Get management go-ahead'] },
        whenType: 'immediately', whenOffset: 0, whenUnit: 'hrs',
        requiresApproval: true, approvers: ['owner'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Venue Booking',
        what: 'Scout and book a venue suitable for 100+ guests with parking, AV, and catering permissions.',
        how: { type: 'sop', sopText: '1. Compare at least 3 venue options.\n2. Verify capacity (min 120 for 100 guests).\n3. Confirm AV equipment and catering allowed.\n4. Verify parking availability.\n5. Get written booking confirmation.' },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Budget Approval',
        what: 'Prepare detailed budget covering venue, catering, AV, decor, invitations, and gifts.',
        how: { type: 'checklist', items: ['Venue charges', 'Catering per head × 100', 'AV & lighting', 'Decor & flowers', 'Printing & stationery', 'Gifts / mementos', 'Contingency 10%'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: true, approvers: ['owner', 'admin'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Invitations & RSVP Tracking',
        what: 'Design and send invitations. Set up RSVP tracking and follow up with non-responders.',
        how: { type: 'sop', sopText: '1. Design digital + physical invitation.\n2. Send digital invites via WhatsApp & Email.\n3. Send printed invites to VIP guests.\n4. Set up RSVP form or tracking sheet.\n5. Chase up RSVPs weekly.' },
        whenType: 'after_previous', whenOffset: 3, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Catering Arrangement',
        what: 'Finalize catering menu for 100 guests. Confirm vegetarian/non-veg split and dietary needs.',
        how: { type: 'checklist', items: ['Finalize menu (veg + non-veg)', 'Confirm dietary restrictions', 'Arrange service staff', 'Confirm crockery & cutlery', 'Arrange water & beverages', 'Confirm serving timeline'] },
        whenType: 'after_previous', whenOffset: 5, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'AV, Decor & Logistics Setup',
        what: 'Coordinate sound, lighting, projector, stage, seating layout, and all decorations.',
        how: { type: 'sop', sopText: '1. Brief AV vendor day before.\n2. Finalize seating layout.\n3. Set up stage, backdrop, podium.\n4. Decor installation (flowers, lighting).\n5. Full sound check and lighting test.\n6. Confirm on-call vendor numbers.' },
        whenType: 'after_previous', whenOffset: 7, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Event Day Execution',
        what: 'Coordinate all activities on the day. Manage guest experience and resolve issues in real time.',
        how: { type: 'checklist', items: ['Welcome desk set up', 'Registration process running', 'Program on schedule', 'Catering timing confirmed', 'Photographer active', 'MC briefed', 'Emergency contacts on standby'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Post-Event Wrap-up',
        what: 'Send thank-you messages, collect feedback, settle vendor payments, share photos, and document learnings.',
        how: { type: 'checklist', items: ['Thank you messages sent', 'Feedback forms collected', 'All vendor payments settled', 'Photo album shared', 'Management report submitted', 'Improvements documented for next year'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: true,
      },
    ],
  },

  {
    id: 'teacher_training',
    name: 'Therapy Teacher Training Course',
    description: 'Structured workflow to plan, run, and conclude a therapy teacher training and certification program.',
    icon: '🎓',
    color: '#0891b2',
    bg: '#ecfeff',
    steps: [
      {
        title: 'Course Planning & Curriculum Approval',
        what: 'Finalize curriculum, duration, modules, assessment criteria, and certification standards.',
        how: { type: 'checklist', items: ['Define modules & topics', 'Set duration per module', 'Plan theory + practical ratio', 'Define assessment & pass criteria', 'Get curriculum approved by lead trainer'] },
        whenType: 'immediately', whenOffset: 0, whenUnit: 'hrs',
        requiresApproval: true, approvers: ['owner'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Trainer & Faculty Confirmation',
        what: 'Confirm lead trainer and assistant faculty. Assign module responsibilities and sign agreements.',
        how: { type: 'sop', sopText: '1. Shortlist qualified trainers.\n2. Confirm availability for all course dates.\n3. Assign specific modules per trainer.\n4. Send official confirmation/agreement letter.\n5. Share full course timetable.' },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Participant Registration',
        what: 'Open registrations, screen applications for eligibility, collect fees, and confirm enrollments.',
        how: { type: 'checklist', items: ['Publish registration form', 'Screen & shortlist applicants', 'Collect course fees', 'Send enrollment confirmation', 'Create master participant list', 'Share pre-course reading material'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Study Material Preparation',
        what: 'Prepare and print course manuals, handouts, reference sheets, and assessment forms.',
        how: { type: 'checklist', items: ['Compile & format course manual', 'Print 1 copy per participant', 'Prepare assessment answer sheets', 'Create daily schedule/timetable', 'Prepare certificate template'] },
        whenType: 'after_previous', whenOffset: 5, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Venue & Equipment Setup',
        what: 'Arrange training space, therapy tables, mats, props, and AV equipment.',
        how: { type: 'checklist', items: ['Book training room', 'Arrange therapy tables/mats', 'Set up projector & screen', 'Props and therapy equipment ready', 'First aid kit present', 'Seating for all participants'] },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Course Delivery',
        what: 'Conduct all training sessions as per the approved timetable. Record attendance daily.',
        how: { type: 'sop', sopText: '1. Start each session on time.\n2. Take attendance at start of every session.\n3. Cover theory module then practical.\n4. Q&A at end of each module.\n5. Share session resources same day.\n6. Collect daily feedback from participants.' },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Assessment & Grading',
        what: 'Conduct written and practical assessments. Grade participants against defined pass criteria.',
        how: { type: 'checklist', items: ['Written test administered', 'Practical demonstration assessed', 'Attendance verified (min 80%)', 'All participants graded', 'Borderline cases reviewed by panel', 'Results compiled'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: true, approvers: ['owner', 'senior_therapist'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Certification & Closing Ceremony',
        what: 'Issue certificates to qualified participants. Conduct closing ceremony and group photo.',
        how: { type: 'sop', sopText: '1. Print and sign all certificates.\n2. Conduct closing ceremony.\n3. Certificate distribution to qualifying participants.\n4. Group photograph.\n5. Collect feedback forms.\n6. Share post-course resources and community group.' },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: true,
      },
    ],
  },

  {
    id: 'outstation_camp',
    name: 'Outstation Camp',
    description: 'Full planning and execution checklist for an outstation therapy, wellness, or team camp.',
    icon: '🏕️',
    color: '#16a34a',
    bg: '#f0fdf4',
    steps: [
      {
        title: 'Location Selection & Approval',
        what: 'Scout camp location. Evaluate facilities, safety, accessibility, and connectivity.',
        how: { type: 'checklist', items: ['Evaluate 2–3 location options', 'Check safety and facilities', 'Assess travel distance from base', 'Confirm mobile connectivity', 'Check food/water availability', 'Get management approval to proceed'] },
        whenType: 'immediately', whenOffset: 0, whenUnit: 'hrs',
        requiresApproval: true, approvers: ['owner'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Budget & Legal Permissions',
        what: 'Prepare full budget. Obtain local authority permissions, group insurance, and waivers.',
        how: { type: 'checklist', items: ['Budget: travel + stay + meals + equipment', 'Local authority permission obtained', 'Group travel insurance arranged', 'Participant consent/waiver forms', 'Medical/emergency plan prepared'] },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: true, approvers: ['owner', 'admin'], isParallel: false, stopFlow: false,
      },
      {
        title: 'Travel Arrangement',
        what: 'Book transport for all participants. Confirm pick-up points and departure schedule.',
        how: { type: 'sop', sopText: '1. Book chartered vehicle or coach.\n2. Confirm driver name and vehicle details.\n3. Set pick-up points and arrival times.\n4. Share travel plan with all participants 48 hrs in advance.' },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Accommodation',
        what: 'Book rooms, dormitories, or tents for all participants and staff.',
        how: { type: 'checklist', items: ['Accommodation booked for all', 'Sleeping arrangements confirmed', 'Separate rooms for staff', 'Washroom facilities adequate', 'Check-in/out times confirmed', 'Emergency exit locations known'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Equipment & Supplies Packing',
        what: 'Pack all equipment, first aid, daily supplies, and emergency materials.',
        how: { type: 'checklist', items: ['Therapy mats & props packed', 'First aid box stocked', 'Basic medicines included', 'Daily activity supplies', 'Emergency contact list printed', 'Camp schedule printed (1 per person)', 'Camera / documentation equipment'] },
        whenType: 'after_previous', whenOffset: 3, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Participant Briefing',
        what: 'Brief all participants on schedule, rules, emergency procedures, and personal packing list.',
        how: { type: 'sop', sopText: '1. Share daily camp schedule.\n2. Explain camp rules and code of conduct.\n3. Emergency procedures briefing.\n4. Share personal packing list checklist.\n5. Collect signed consent forms.\n6. Confirm final headcount.' },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Camp Execution',
        what: 'Run all planned sessions, activities, and meals as per the daily schedule.',
        how: { type: 'sop', sopText: '1. Daily morning assembly & attendance.\n2. Sessions as per schedule.\n3. Meal coordination with local caterer.\n4. Evening debrief / reflection.\n5. Maintain daily incident/activity log.\n6. Ensure safety guidelines followed at all times.' },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Pack Up & Safe Return',
        what: 'Ensure all equipment is packed, accommodation checked out, and safe return journey.',
        how: { type: 'checklist', items: ['All equipment packed and counted', 'Room/tent check-out done', 'Property left clean', 'Final headcount before departure', 'Return journey on schedule', 'Lost & found checked'] },
        whenType: 'after_previous', whenOffset: 1, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: false,
      },
      {
        title: 'Post-Camp Report & Follow-up',
        what: 'Document activities, compile photos, tally expenses, gather feedback, and report to management.',
        how: { type: 'checklist', items: ['Compile and share photo gallery', 'Collect feedback forms from all', 'Submit full expense report', 'Write camp summary report', 'Identify improvements for next camp', 'Submit report to management'] },
        whenType: 'after_previous', whenOffset: 2, whenUnit: 'days',
        requiresApproval: false, approvers: [], isParallel: false, stopFlow: true,
      },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyStep = () => ({
  stepNumber: 1,
  title: '',
  what: '',
  how: { type: 'sop', sopText: '', link: '', items: [''] },
  executor: '',
  executorName: '',
  owner: '',
  ownerName: '',
  whenType: 'after_previous',
  whenOffset: 2,
  whenUnit: 'hrs',
  deadlineType: 'auto',
  deadline: '',
  requiresApproval: false,
  approvers: [],
  isParallel: false,
  stopFlow: false,
  status: 'pending',
});

const emptyFlow = () => ({
  name: '',
  description: '',
  context: '',
  templateId: '',
  steps: [],
});

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function isOverdue(step) {
  if (!step.deadline) return false;
  if (['completed', 'approved', 'skipped'].includes(step.status)) return false;
  return new Date(step.deadline) < new Date();
}

function progressOf(flow) {
  if (!flow.steps.length) return 0;
  const done = flow.steps.filter(s => ['completed', 'approved'].includes(s.status)).length;
  return Math.round((done / flow.steps.length) * 100);
}

// Group steps into Gantt columns: parallel steps share the same column as previous
function buildGanttCols(steps) {
  const cols = [];
  steps.forEach((step, idx) => {
    if (idx === 0 || !step.isParallel) {
      cols.push([step]);
    } else {
      cols[cols.length - 1].push(step);
    }
  });
  return cols;
}

// ─── Step Modal — FMS2 design ─────────────────────────────────────────────────

function CreateStepModal({ step, stepNumber, users, onSave, onClose }) {
  const [form, setForm] = useState({ ...emptyStep(), ...step, stepNumber });
  const [showDep, setShowDep] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setHow = (key, val) => setForm(f => ({ ...f, how: { ...f.how, [key]: val } }));
  const addHowItem = () => setForm(f => ({ ...f, how: { ...f.how, items: [...(f.how.items || []), ''] } }));
  const updateHowItem = (idx, val) => {
    const items = [...(form.how.items || [])]; items[idx] = val;
    setForm(f => ({ ...f, how: { ...f.how, items } }));
  };
  const removeHowItem = idx => setForm(f => ({ ...f, how: { ...f.how, items: (f.how.items || []).filter((_, i) => i !== idx) } }));
  const toggleApprover = val => {
    const list = form.approvers || [];
    set('approvers', list.includes(val) ? list.filter(a => a !== val) : [...list, val]);
  };
  const handleSave = () => {
    if (!form.title.trim()) { alert('Step title is required'); return; }
    onSave(form);
  };

  const ROW = { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '13px 20px', borderBottom: '1px solid #f0f2f5' };
  const ICON = (bg) => ({ width: 38, height: 38, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0, marginTop: 1 });
  const RLBL = { fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 18, width: 550, maxWidth: '97vw', maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 28px 90px rgba(0,0,0,0.38)', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Dark navy header */}
        <div style={{ background: '#0b2343', color: '#fff', padding: '18px 22px 16px', borderRadius: '18px 18px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 22 }}>⚡</span>
              {step?.title ? 'Edit Step' : 'Create Next Step'}
            </div>
            <div style={{ fontSize: 10, opacity: 0.55, marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 600 }}>
              ADD NEXT STEP · WHO · WHEN · OWNER — Step {stepNumber}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>

        {/* Title */}
        <div style={{ ...ROW, background: '#f8fafc' }}>
          <div style={ICON('#e0e7ff')}>📝</div>
          <div style={{ flex: 1 }}>
            <div style={RLBL}>Step Title *</div>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="What is this step called?" style={inp} autoFocus />
          </div>
        </div>

        {/* Who */}
        <div style={ROW}>
          <div style={ICON('#dbeafe')}>👤</div>
          <div style={{ flex: 1 }}>
            <div style={RLBL}>Who — Executor (does the work)</div>
            <select value={form.executor} onChange={e => {
              const u = users.find(x => x.username === e.target.value);
              set('executor', e.target.value); set('executorName', u?.name || e.target.value);
            }} style={inp}>
              <option value="">Select executor…</option>
              {users.map(u => <option key={u.username} value={u.username}>{u.name || u.username}</option>)}
            </select>
          </div>
        </div>

        {/* Owner */}
        <div style={{ ...ROW, background: '#fafafa' }}>
          <div style={ICON('#fce7f3')}>🏷️</div>
          <div style={{ flex: 1 }}>
            <div style={RLBL}>Owner — Responsible Person</div>
            <select value={form.owner} onChange={e => {
              const u = users.find(x => x.username === e.target.value);
              set('owner', e.target.value); set('ownerName', u?.name || e.target.value);
            }} style={inp}>
              <option value="">Select owner…</option>
              {users.map(u => <option key={u.username} value={u.username}>{u.name || u.username}</option>)}
            </select>
          </div>
        </div>

        {/* What */}
        <div style={{ ...ROW, position: 'relative' }}>
          <div style={ICON('#d1fae5')}>📋</div>
          <div style={{ flex: 1 }}>
            <div style={RLBL}>What — Task Description</div>
            <textarea value={form.what} onChange={e => set('what', e.target.value)}
              placeholder="Describe exactly what needs to be done in this step…"
              rows={3} style={{ ...inp, resize: 'vertical', paddingRight: 34 }} />
            <span style={{ position: 'absolute', right: 24, bottom: 22, fontSize: 14, color: '#cbd5e1', pointerEvents: 'none' }}>✏️</span>
          </div>
        </div>

        {/* How */}
        <div style={{ ...ROW, background: '#fafafa' }}>
          <div style={ICON('#fef3c7')}>⚙️</div>
          <div style={{ flex: 1 }}>
            <div style={RLBL}>How — Method</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {[
                { value: 'checklist', icon: '☑️', label: 'Checklist',   activeBg: '#dcfce7', activeBorder: '#4ade80' },
                { value: 'sop',       icon: '📄', label: 'SOP / Notes', activeBg: '#dbeafe', activeBorder: '#60a5fa' },
                { value: 'link',      icon: '🔗', label: 'Link',        activeBg: '#e9d5ff', activeBorder: '#a78bfa' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setHow('type', opt.value)}
                  style={{ flex: 1, padding: '12px 6px', borderRadius: 10,
                    border: `2px solid ${form.how.type === opt.value ? opt.activeBorder : '#e5e7eb'}`,
                    background: form.how.type === opt.value ? opt.activeBg : '#fff',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 22 }}>{opt.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 11, color: form.how.type === opt.value ? '#1e3a5f' : '#9ca3af' }}>{opt.label}</span>
                </button>
              ))}
            </div>
            {form.how.type === 'checklist' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(form.how.items || ['']).map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6 }}>
                    <input value={item} onChange={e => updateHowItem(idx, e.target.value)}
                      placeholder={`Item ${idx + 1}…`} style={{ ...inp, flex: 1 }} />
                    <button onClick={() => removeHowItem(idx)} style={dangerBtn}>✕</button>
                  </div>
                ))}
                <button onClick={addHowItem} style={{ ...outBtn, fontSize: 12, padding: '5px 12px', alignSelf: 'flex-start' }}>+ Add Item</button>
              </div>
            )}
            {form.how.type === 'sop' && (
              <textarea value={form.how.sopText} onChange={e => setHow('sopText', e.target.value)}
                placeholder="SOP steps, guidelines, or notes…" rows={5} style={{ ...inp, resize: 'vertical' }} />
            )}
            {form.how.type === 'link' && (
              <input value={form.how.link} onChange={e => setHow('link', e.target.value)}
                placeholder="https://…" style={inp} />
            )}
          </div>
        </div>

        {/* When */}
        <div style={ROW}>
          <div style={ICON('#fce7f3')}>🕐</div>
          <div style={{ flex: 1 }}>
            <div style={RLBL}>When</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => set('whenType', 'immediately')}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 20,
                  background: form.whenType === 'immediately' ? '#0b2343' : '#f1f5f9',
                  color: form.whenType === 'immediately' ? '#fff' : '#64748b',
                  border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                {form.whenType === 'immediately' && <span style={{ fontSize: 11 }}>✓</span>}
                Immediately
              </button>
              <span style={{ color: '#cbd5e1', fontWeight: 900, fontSize: 16 }}>›</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5,
                background: form.whenType === 'after_previous' ? '#eff6ff' : '#f8fafc',
                border: `1.5px solid ${form.whenType === 'after_previous' ? '#93c5fd' : '#e2e8f0'}`,
                borderRadius: 20, padding: '6px 12px', cursor: 'pointer' }}
                onClick={() => set('whenType', 'after_previous')}>
                <span>⏳</span>
                <input type="number" min={1} value={form.whenOffset}
                  onChange={e => { set('whenType', 'after_previous'); set('whenOffset', Number(e.target.value)); }}
                  style={{ width: 38, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 800, textAlign: 'center', outline: 'none', color: '#1e40af' }} />
                <select value={form.whenUnit} onChange={e => { set('whenType', 'after_previous'); set('whenUnit', e.target.value); }}
                  style={{ border: 'none', background: 'transparent', fontSize: 12, outline: 'none', cursor: 'pointer', fontWeight: 600 }}>
                  <option value="hrs">hrs</option>
                  <option value="days">days</option>
                </select>
                <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>after current step</span>
              </div>
            </div>
            <button onClick={() => set('whenType', 'specific_date')}
              style={{ marginTop: 8, background: 'none', border: 'none', fontSize: 11, color: '#2563eb', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              {form.whenType === 'specific_date' ? '▼' : '+'} Set specific date/time
            </button>
            {form.whenType === 'specific_date' && (
              <input type="datetime-local" value={form.deadline}
                onChange={e => { set('deadline', e.target.value); set('deadlineType', 'specific'); }}
                style={{ ...inp, marginTop: 6, width: 260 }} />
            )}
          </div>
        </div>

        {/* Step dependency */}
        <div style={{ ...ROW, background: '#fafafa' }}>
          <div style={ICON('#e0f2fe')}>🔗</div>
          <div style={{ flex: 1 }}>
            <button onClick={() => setShowDep(d => !d)}
              style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>
              {showDep ? '— Hide' : '+ Add'} step dependency…
            </button>
            {showDep && (
              <div style={{ marginTop: 8 }}>
                <input value={form.parallelGroup} onChange={e => set('parallelGroup', e.target.value)}
                  placeholder="Parallel group ID (steps with same ID run together)"
                  style={inp} />
              </div>
            )}
          </div>
        </div>

        {/* Bottom toggles */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #f0f2f5', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer',
              padding: '8px 14px', borderRadius: 8, fontWeight: 700,
              background: form.isParallel ? '#fef9c3' : '#f8fafc',
              border: `1.5px solid ${form.isParallel ? '#fcd34d' : '#e2e8f0'}` }}>
              <input type="checkbox" checked={form.isParallel} onChange={e => set('isParallel', e.target.checked)} />
              🔀 Parallel Next Step
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer',
              padding: '8px 14px', borderRadius: 8, fontWeight: 700,
              background: form.stopFlow ? '#fff1f2' : '#f8fafc',
              border: `1.5px solid ${form.stopFlow ? '#fda4af' : '#e2e8f0'}` }}>
              <input type="checkbox" checked={form.stopFlow} onChange={e => set('stopFlow', e.target.checked)} />
              🛑 Stop Flow After This
            </label>
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '11px 14px', border: '1.5px solid #e2e8f0' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: form.requiresApproval ? 10 : 0, color: '#1e3a5f' }}>
              <input type="checkbox" checked={form.requiresApproval} onChange={e => set('requiresApproval', e.target.checked)} style={{ width: 16, height: 16 }} />
              🔐 Is Approval Required?
            </label>
            {form.requiresApproval && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {APPROVER_OPTIONS.map(opt => {
                  const active = (form.approvers || []).includes(opt.value);
                  return (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer',
                      padding: '6px 13px', borderRadius: 20, fontWeight: 700,
                      background: active ? '#0b2343' : '#f1f5f9',
                      color: active ? '#fff' : '#475569',
                      border: `1.5px solid ${active ? '#0b2343' : '#e2e8f0'}` }}>
                      <input type="checkbox" checked={active} onChange={() => toggleApprover(opt.value)} style={{ display: 'none' }} />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', background: '#f8fafc', borderRadius: '0 0 18px 18px', flexShrink: 0 }}>
          <button onClick={onClose} style={outBtn}>Cancel</button>
          <button onClick={handleSave} style={{ ...primBtn, background: '#0b2343', padding: '10px 30px', fontSize: 14 }}>✓ Save Step</button>
        </div>
      </div>
    </div>
  );
}

// ─── Template Preview Modal ────────────────────────────────────────────────────

function TemplatePreviewModal({ template, onUse, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, width: 620, maxWidth: '97vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ background: template.color, color: '#fff', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>{template.icon} {template.name}</div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{template.description}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', borderRadius: 8, padding: '4px 10px' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {template.steps.length} Steps in this template
          </div>

          {template.steps.map((step, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: template.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                {idx + 1}
              </div>
              <div style={{ flex: 1, background: '#fafafa', borderRadius: 10, padding: '10px 14px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f', marginBottom: 3 }}>{step.title}</div>
                {step.what && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 5 }}>{step.what}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
                  <span style={{ background: '#f3f4f6', borderRadius: 6, padding: '2px 8px', color: '#374151' }}>
                    {step.how.type === 'checklist' ? `☑️ Checklist (${step.how.items.length} items)` : step.how.type === 'sop' ? '📄 SOP' : '🔗 Link'}
                  </span>
                  <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: 6, padding: '2px 8px' }}>
                    ⏰ {step.whenType === 'immediately' ? 'Immediately' : `+${step.whenOffset} ${step.whenUnit}`}
                  </span>
                  {step.requiresApproval && (
                    <span style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '2px 8px' }}>🔐 Approval required</span>
                  )}
                  {step.stopFlow && (
                    <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 6, padding: '2px 8px' }}>🛑 Stop Flow</span>
                  )}
                  {step.isParallel && (
                    <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px' }}>🔀 Parallel</span>
                  )}
                </div>
              </div>
              {idx < template.steps.length - 1 && (
                <div style={{ position: 'absolute', width: 2, height: 12, background: '#e5e7eb', marginLeft: 13, marginTop: 28, display: 'none' }} />
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <button onClick={onClose} style={outBtn}>Close</button>
            <button onClick={() => onUse(template)} style={{ ...primBtn, background: template.color }}>
              ⚡ Use this Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Flow Builder ──────────────────────────────────────────────────────────────

function FlowBuilder({ flow: initialFlow, users, onSave, onBack }) {
  const [flow, setFlow] = useState(initialFlow || emptyFlow());
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStepIdx, setEditingStepIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const setMeta = (key, val) => setFlow(f => ({ ...f, [key]: val }));

  const handleStepSave = (stepData) => {
    setFlow(f => {
      let steps = editingStepIdx !== null
        ? f.steps.map((s, i) => i === editingStepIdx ? { ...s, ...stepData } : s)
        : [...f.steps, { ...stepData, stepNumber: f.steps.length + 1, status: 'pending' }];
      steps = steps.map((s, i) => ({ ...s, stepNumber: i + 1 }));
      return { ...f, steps };
    });
    setShowStepModal(false);
  };

  const deleteStep = idx => setFlow(f => ({
    ...f, steps: f.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1 })),
  }));

  const moveStep = (idx, dir) => {
    setFlow(f => {
      const steps = [...f.steps];
      const target = idx + dir;
      if (target < 0 || target >= steps.length) return f;
      [steps[idx], steps[target]] = [steps[target], steps[idx]];
      return { ...f, steps: steps.map((s, i) => ({ ...s, stepNumber: i + 1 })) };
    });
  };

  const handleSave = async () => {
    if (!flow.name.trim()) { alert('Flow name is required'); return; }
    setSaving(true);
    try { await onSave(flow); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={outBtn}>← Dashboard</button>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#1e3a5f' }}>⚡ Flow Builder</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Build your step-by-step workflow</div>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} style={{ ...primBtn, opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳ Saving…' : '💾 Save Flow'}
        </button>
      </div>

      {/* Flow Metadata */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Flow Name *</label>
            <input value={flow.name} onChange={e => setMeta('name', e.target.value)}
              placeholder="e.g. Order #1042 Dispatch, Annual Day 2025, December Camp…"
              style={{ ...inp, fontSize: 16, fontWeight: 700 }} autoFocus />
          </div>
          <div>
            <label style={lbl}>Subject / Reference (optional)</label>
            <input value={flow.context} onChange={e => setMeta('context', e.target.value)}
              placeholder="Brief context — who, what, or which event this is for" style={inp} />
          </div>
          <div>
            <label style={lbl}>Description (optional)</label>
            <input value={flow.description} onChange={e => setMeta('description', e.target.value)}
              placeholder="Purpose or notes about this flow" style={inp} />
          </div>
        </div>
      </div>

      {/* START node */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 6, marginBottom: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>START</div>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Flow begins</span>
      </div>

      {/* Step Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {flow.steps.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: '#fff', borderRadius: 14, border: '2px dashed #d1d5db', color: '#9ca3af', marginLeft: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⚡</div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>No steps yet</div>
            <div style={{ fontSize: 13 }}>Click "+ Add Step" to build your workflow</div>
          </div>
        )}

        {flow.steps.map((step, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 0, alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                {step.stepNumber}
              </div>
              {idx < flow.steps.length - 1 && <div style={{ width: 2, flex: 1, background: '#dbeafe', minHeight: 16 }} />}
            </div>

            <div style={{ flex: 1, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 10, marginLeft: 8, boxShadow: '0 1px 5px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              {/* Card header */}
              <div style={{ background: '#f8fafc', padding: '9px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#1e3a5f' }}>
                  {step.title}
                  {step.stopFlow && <span style={{ marginLeft: 8, fontSize: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '2px 6px' }}>🛑 Stop</span>}
                  {step.isParallel && <span style={{ marginLeft: 6, fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 6px' }}>🔀 Parallel</span>}
                  {step.requiresApproval && <span style={{ marginLeft: 6, fontSize: 10, background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '2px 6px' }}>🔐 Approval</span>}
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} style={{ ...iconBtn, opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveStep(idx, 1)} disabled={idx === flow.steps.length - 1} style={{ ...iconBtn, opacity: idx === flow.steps.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => { setEditingStepIdx(idx); setShowStepModal(true); }} style={{ ...iconBtn, color: '#2563eb' }}>✏️ Edit</button>
                  <button onClick={() => deleteStep(idx)} style={{ ...iconBtn, color: '#dc2626' }}>🗑️</button>
                </div>
              </div>

              {/* Card body */}
              <div style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px', fontSize: 12 }}>
                <div><span style={mLbl}>👤 Executor</span><div style={mVal}>{step.executorName || step.executor || '—'}</div></div>
                <div><span style={mLbl}>🏷️ Owner</span><div style={mVal}>{step.ownerName || step.owner || '—'}</div></div>
                <div><span style={mLbl}>⏰ When</span>
                  <div style={mVal}>{step.whenType === 'immediately' ? 'Immediately' : step.whenType === 'after_previous' ? `+${step.whenOffset} ${step.whenUnit} after prev` : fmtDate(step.deadline)}</div>
                </div>
                {step.what && <div style={{ gridColumn: '1 / -1' }}><span style={mLbl}>📋 What</span><div style={{ ...mVal, color: '#374151' }}>{step.what}</div></div>}
                {step.how?.type === 'checklist' && step.how.items?.filter(Boolean).length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={mLbl}>☑️ Checklist</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                      {step.how.items.filter(Boolean).map((item, i) => <span key={i} style={{ background: '#f3f4f6', borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>• {item}</span>)}
                    </div>
                  </div>
                )}
                {step.how?.type === 'sop' && step.how.sopText && (
                  <div style={{ gridColumn: '1 / -1' }}><span style={mLbl}>📄 SOP</span><div style={{ ...mVal, color: '#374151', whiteSpace: 'pre-wrap', fontSize: 11 }}>{step.how.sopText}</div></div>
                )}
                {step.how?.type === 'link' && step.how.link && (
                  <div style={{ gridColumn: '1 / -1' }}><span style={mLbl}>🔗 Link</span><a href={step.how.link} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 12 }}>{step.how.link}</a></div>
                )}
                {step.requiresApproval && step.approvers?.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}><span style={mLbl}>🔐 Approvers</span><div style={mVal}>{step.approvers.map(a => APPROVER_OPTIONS.find(o => o.value === a)?.label || a).join(', ')}</div></div>
                )}
              </div>

              <div style={{ padding: '6px 14px 10px' }}>
                <button onClick={() => { setEditingStepIdx(null); setShowStepModal(true); }}
                  style={{ ...outBtn, fontSize: 11, padding: '4px 12px' }}>+ Add Next Step</button>
              </div>
            </div>
          </div>
        ))}

        {/* END node */}
        {flow.steps.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 6, marginTop: 4 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#16a34a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>END</div>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Flow complete</span>
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
          <button onClick={() => { setEditingStepIdx(null); setShowStepModal(true); }}
            style={{ ...primBtn, padding: '12px 40px', fontSize: 15 }}>+ Add Step</button>
        </div>
      </div>

      {showStepModal && (
        <CreateStepModal
          step={editingStepIdx !== null ? flow.steps[editingStepIdx] : null}
          stepNumber={editingStepIdx !== null ? flow.steps[editingStepIdx].stepNumber : flow.steps.length + 1}
          users={users}
          onSave={handleStepSave}
          onClose={() => setShowStepModal(false)}
        />
      )}
    </div>
  );
}

// ─── Templates Tab ─────────────────────────────────────────────────────────────

function TemplatesTab({ onUseTemplate }) {
  const [preview, setPreview] = useState(null);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#1e3a5f', marginBottom: 4 }}>📋 Ready-to-use Flow Templates</div>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Click a template to preview all steps, then use it as a starting point — all steps are editable.</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 16 }}>
        {FLOW_TEMPLATES.map(tpl => (
          <div key={tpl.id} style={{ background: tpl.bg, borderRadius: 14, border: `2px solid ${tpl.color}30`, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column' }}>
            {/* Template card header */}
            <div style={{ background: tpl.color, padding: '16px 18px', color: '#fff' }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>{tpl.icon}</div>
              <div style={{ fontWeight: 900, fontSize: 16, lineHeight: 1.2 }}>{tpl.name}</div>
            </div>

            <div style={{ padding: '14px 18px', flex: 1 }}>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 12 }}>{tpl.description}</div>

              {/* Step preview list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
                {tpl.steps.map((step, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: tpl.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                      {idx + 1}
                    </div>
                    <div style={{ color: '#374151', lineHeight: 1.4 }}>
                      {step.title}
                      {step.requiresApproval && <span style={{ marginLeft: 5, fontSize: 10, color: '#6d28d9' }}>🔐</span>}
                      {step.stopFlow && <span style={{ marginLeft: 5, fontSize: 10, color: '#dc2626' }}>🛑</span>}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 14 }}>
                {tpl.steps.length} steps · {tpl.steps.filter(s => s.requiresApproval).length} approval point{tpl.steps.filter(s => s.requiresApproval).length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding: '0 18px 16px', display: 'flex', gap: 8 }}>
              <button onClick={() => setPreview(tpl)}
                style={{ ...outBtn, flex: 1, fontSize: 12, padding: '7px 0', textAlign: 'center' }}>
                👁️ Preview
              </button>
              <button onClick={() => onUseTemplate(tpl)}
                style={{ flex: 1, padding: '7px 0', background: tpl.color, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                ⚡ Use Template
              </button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <TemplatePreviewModal
          template={preview}
          onUse={tpl => { setPreview(null); onUseTemplate(tpl); }}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

// ─── Gantt View — KIT campaign builder style ──────────────────────────────────

// Status → card colour (mirrors KIT typeColors)
const STEP_COLORS = {
  pending:    { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' },
  in_progress:{ bg: '#dbeafe', border: '#2563eb', text: '#1e3a5f' },
  completed:  { bg: '#dcfce7', border: '#16a34a', text: '#14532d' },
  approved:   { bg: '#d1fae5', border: '#059669', text: '#064e3b' },
  blocked:    { bg: '#fee2e2', border: '#dc2626', text: '#7f1d1d' },
  skipped:    { bg: '#f9fafb', border: '#e5e7eb', text: '#9ca3af' },
  overdue:    { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' },
};

function GanttStepCard({ step, flow, onMarkStepDone, auth }) {
  const overdue = isOverdue(step);
  const ssm = STEP_STATUS_META[step.status] || STEP_STATUS_META.pending;
  const tc = overdue ? STEP_COLORS.overdue : (STEP_COLORS[step.status] || STEP_COLORS.pending);

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: 10,
      boxShadow: step.status === 'in_progress'
        ? '0 0 0 2px #2563eb, 0 4px 14px rgba(37,99,235,0.18)'
        : '0 2px 8px rgba(0,0,0,0.09)',
      width: 188, minWidth: 188,
      borderTop: `4px solid ${tc.border}`,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Coloured header band */}
      <div style={{
        background: tc.bg, color: tc.text,
        padding: '7px 11px', borderRadius: '6px 6px 0 0',
        fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>{ssm.icon} Step {step.stepNumber}</span>
        <span style={{ display: 'flex', gap: 3 }}>
          {overdue && <span>⚠️</span>}
          {step.requiresApproval && <span>🔐</span>}
          {step.isParallel && <span style={{ opacity: 0.6 }}>‖</span>}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: '9px 11px', flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 3, lineHeight: 1.3 }}>
          {step.title}
        </div>
        {(step.executorName || step.executor) && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 20, padding: '2px 8px', marginBottom: 5,
            fontSize: 11, fontWeight: 700, color: '#1d4ed8',
          }}>
            👤 {step.executorName || step.executor}
          </div>
        )}
        {step.what && (
          <div style={{
            fontSize: 11, color: '#94a3b8', lineHeight: 1.45,
            maxHeight: 46, overflow: 'hidden',
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
          }}>{step.what}</div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '7px 11px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <span style={{ background: ssm.bg, color: ssm.color, fontSize: 9, fontWeight: 700, borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {ssm.label}
          </span>
          {step.stopFlow && <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: 9, borderRadius: 4, padding: '1px 5px' }}>🛑 Stop</span>}
        </div>
        {step.deadline && (
          <div style={{ fontSize: 10, color: overdue ? '#dc2626' : '#94a3b8', fontWeight: overdue ? 700 : 400 }}>
            {overdue ? '⚠️ ' : '📅 '}{fmtDate(step.deadline)}
          </div>
        )}
        {step.completedAt && (
          <div style={{ fontSize: 9, color: '#10b981', fontWeight: 600 }}>✅ {fmtDate(step.completedAt)}</div>
        )}
        {step.status === 'in_progress' && (
          <button onClick={() => onMarkStepDone(flow._id, step._id, auth.username, '')}
            style={{ width: '100%', padding: '5px 0', marginTop: 2, background: 'linear-gradient(135deg, #2563eb, #1e40af)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
            ✓ Mark Done
          </button>
        )}
      </div>
    </div>
  );
}

function GanttView({ flows, onMarkStepDone, onEdit, onActivate, onDelete }) {
  const { auth } = useAuth();

  if (flows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 24px', color: '#9ca3af' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>No flows yet</div>
        <div style={{ fontSize: 13, marginTop: 6 }}>Create a flow or use a template to get started</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {flows.map(flow => {
        const cols = buildGanttCols(flow.steps);
        const sm = STATUS_META[flow.status] || STATUS_META.draft;
        const pct = progressOf(flow);
        const hasOverdue = flow.steps.some(s => isOverdue(s));

        return (
          <div key={flow._id} style={{
            background: '#fff', borderRadius: 14, overflow: 'hidden',
            border: `1px solid ${hasOverdue ? '#fca5a5' : flow.status === 'active' ? '#bfdbfe' : '#e5e7eb'}`,
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          }}>
            {/* Flow header */}
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#1e3a5f' }}>{flow.name}</div>
                {flow.context && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>📌 {flow.context}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ background: sm.bg, color: sm.color, fontSize: 11, fontWeight: 800, borderRadius: 8, padding: '3px 10px' }}>{sm.label}</span>
                <div style={{ minWidth: 90 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>
                    <span>{flow.steps.filter(s => ['completed','approved'].includes(s.status)).length}/{flow.steps.length}</span>
                    <span>{pct}%</span>
                  </div>
                  <div style={{ height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#16a34a' : '#2563eb', borderRadius: 3 }} />
                  </div>
                </div>
                {hasOverdue && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 800 }}>⚠️ Overdue</span>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {flow.status === 'draft' && (
                  <button onClick={() => onActivate(flow._id)}
                    style={{ fontSize: 11, padding: '5px 12px', background: 'linear-gradient(135deg, #10b981, #0d9488)', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700 }}>▶ Start</button>
                )}
                <button onClick={() => onEdit(flow)}
                  style={{ fontSize: 11, padding: '5px 11px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}>✏️ Edit</button>
                <button onClick={() => { if (window.confirm('Delete this flow?')) onDelete(flow._id); }}
                  style={{ fontSize: 11, padding: '5px 11px', background: '#fff5f5', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}>🗑️</button>
              </div>
            </div>

            {/* Horizontal card strip */}
            <div style={{ padding: '16px 18px 18px', overflowX: 'auto' }}>
              {flow.steps.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>No steps yet — click Edit to add steps.</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
                  {/* START chip */}
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: 0, alignSelf: 'center' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #10b981, #0d9488)',
                      color: '#fff', padding: '7px 22px 7px 14px', borderRadius: 6,
                      fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
                      clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
                      whiteSpace: 'nowrap',
                    }}>▶ START</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#94a3b8', fontSize: 22, alignSelf: 'center' }}>→</div>

                  {/* Step column groups */}
                  {cols.map((colSteps, colIdx) => {
                    const nextCol = cols[colIdx + 1];
                    const nextStep = nextCol && nextCol[0];
                    const timing = nextStep && nextStep.whenType === 'after_previous' && nextStep.whenOffset > 0
                      ? `+${nextStep.whenOffset}${nextStep.whenUnit === 'days' ? 'd' : 'h'}`
                      : null;
                    return (
                    <React.Fragment key={colIdx}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {colSteps.map((step, sIdx) => (
                          <GanttStepCard
                            key={step._id || sIdx}
                            step={step}
                            flow={flow}
                            onMarkStepDone={onMarkStepDone}
                            auth={auth}
                          />
                        ))}
                      </div>

                      {colIdx < cols.length - 1 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 5px', alignSelf: 'center', minWidth: 40 }}>
                          <span style={{ color: '#94a3b8', fontSize: 22, lineHeight: 1 }}>→</span>
                          {timing && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '1px 4px', marginTop: 2, whiteSpace: 'nowrap' }}>
                              {timing}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, alignSelf: 'center', paddingLeft: 6 }}>
                          <span style={{ color: '#94a3b8', fontSize: 22 }}>→</span>
                          <div style={{
                            background: flow.status === 'completed' ? 'linear-gradient(135deg, #16a34a, #0d9488)' : '#e2e8f0',
                            color: flow.status === 'completed' ? '#fff' : '#94a3b8',
                            padding: '7px 14px', borderRadius: 6,
                            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                          }}>
                            {flow.status === 'completed' ? '✅ DONE' : '🏁 END'}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );})}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Flow Dashboard View ───────────────────────────────────────────────────────

function FlowDashboardView({ flows, onNew, onEdit, onActivate, onDelete, onMarkStepDone }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedFlow, setExpandedFlow] = useState(null);
  const [gView, setGView] = useState('gantt');
  const { auth } = useAuth();

  const filtered = flows.filter(f => {
    const matchStatus = filter === 'all' || f.status === filter;
    const matchSearch = !search || f.name.toLowerCase().includes(search.toLowerCase())
      || (f.context || '').toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const tabStyle = (active) => ({
    padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
    background: active ? '#2563eb' : '#f3f4f6',
    color: active ? '#fff' : '#374151',
  });

  const viewBtnStyle = (active) => ({
    padding: '6px 14px', border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`,
    background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#6b7280',
    borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12,
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#1e3a5f' }}>⚡ All Flows</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            {flows.length} flows · {flows.filter(f => f.status === 'active').length} active
            {flows.some(f => f.steps.some(s => isOverdue(s))) ? ' · ⚠️ Some steps overdue' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', gap: 3, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
            <button onClick={() => setGView('gantt')} style={viewBtnStyle(gView === 'gantt')}>⬛ Gantt</button>
            <button onClick={() => setGView('list')}  style={viewBtnStyle(gView === 'list')}>☰ List</button>
          </div>
          <button onClick={onNew} style={{ ...primBtn, padding: '10px 22px', fontSize: 13 }}>+ New Flow</button>
        </div>
      </div>

      {/* Status filter + search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        {['all', 'active', 'draft', 'paused', 'completed'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={tabStyle(filter === s)}>
            {s === 'all' ? `All (${flows.length})` : `${STATUS_META[s]?.label} (${flows.filter(f => f.status === s).length})`}
          </button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search flows…"
          style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 20, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', width: 200 }} />
      </div>

      {/* Gantt view */}
      {gView === 'gantt' && (
        <GanttView
          flows={filtered}
          onMarkStepDone={onMarkStepDone}
          onEdit={onEdit}
          onActivate={onActivate}
          onDelete={onDelete}
        />
      )}

      {/* List view */}
      {gView === 'list' && (filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 24px', color: '#9ca3af' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>No flows here</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Create a new flow or use a template</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(flow => {
            const sm = STATUS_META[flow.status] || STATUS_META.draft;
            const pct = progressOf(flow);
            const isExpanded = expandedFlow === flow._id;
            const activeStep = flow.steps.find(s => s.status === 'in_progress');
            const hasOverdue = flow.steps.some(s => isOverdue(s));

            return (
              <div key={flow._id}
                style={{ background: '#fff', borderRadius: 12, border: `1px solid ${hasOverdue ? '#fca5a5' : flow.status === 'active' ? '#bfdbfe' : '#e5e7eb'}`,
                  boxShadow: `0 2px 8px rgba(0,0,0,${flow.status === 'active' ? '0.07' : '0.03'})`, overflow: 'hidden' }}>

                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', cursor: 'pointer' }}
                  onClick={() => setExpandedFlow(isExpanded ? null : flow._id)}>
                  <span style={{ background: sm.bg, color: sm.color, borderRadius: 8, padding: '3px 10px', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{sm.label}</span>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#1e3a5f' }}>{flow.name}</div>
                    {flow.context && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📌 {flow.context}</div>}
                  </div>
                  <div style={{ width: 130, flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 3 }}>
                      <span>{flow.steps.filter(s => ['completed', 'approved'].includes(s.status)).length}/{flow.steps.length} steps</span>
                      <span>{pct}%</span>
                    </div>
                    <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#16a34a' : '#2563eb', borderRadius: 3 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', minWidth: 130, flexShrink: 0 }}>
                    {activeStep ? <span>🔄 {activeStep.title}</span> : flow.status === 'completed' ? <span style={{ color: '#16a34a' }}>✅ All done</span> : <span style={{ color: '#9ca3af' }}>Not started</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{fmtDate(flow.createdAt)}</div>
                  {hasOverdue && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 800 }}>⚠️ Overdue</span>}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {flow.status === 'draft' && (
                      <button onClick={() => onActivate(flow._id)} style={{ ...primBtn, fontSize: 11, padding: '5px 14px', background: '#16a34a' }}>▶ Start</button>
                    )}
                    <button onClick={() => onEdit(flow)} style={{ ...outBtn, fontSize: 11, padding: '5px 12px' }}>✏️ Edit</button>
                    <button onClick={() => { if (window.confirm('Delete this flow?')) onDelete(flow._id); }}
                      style={{ ...outBtn, fontSize: 11, padding: '5px 12px', color: '#dc2626', borderColor: '#fca5a5' }}>🗑️</button>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f0f4f8', padding: '14px 18px', background: '#fafbff' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Step Timeline</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {flow.steps.map((step, sIdx) => {
                        const ssm = STEP_STATUS_META[step.status] || STEP_STATUS_META.pending;
                        const overdue = isOverdue(step);
                        return (
                          <div key={sIdx} style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                            background: overdue ? '#fff7f7' : step.status === 'in_progress' ? '#eff6ff' : '#fff',
                            borderRadius: 10, padding: '10px 14px', border: `1px solid ${overdue ? '#fca5a5' : step.status === 'in_progress' ? '#bfdbfe' : '#e5e7eb'}` }}>
                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: ssm.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, border: `2px solid ${ssm.color}` }}>
                              {ssm.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>Step {step.stepNumber}: {step.title}</span>
                                <span style={{ background: ssm.bg, color: ssm.color, fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '2px 7px' }}>{ssm.label}</span>
                                {overdue && <span style={{ color: '#dc2626', fontSize: 11, fontWeight: 700 }}>⚠️ OVERDUE</span>}
                                {step.requiresApproval && <span style={{ fontSize: 10, background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '2px 7px' }}>🔐</span>}
                              </div>
                              {step.what && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{step.what}</div>}
                              <div style={{ display: 'flex', gap: 14, marginTop: 4, fontSize: 11, color: '#9ca3af', flexWrap: 'wrap' }}>
                                {step.executorName && <span>👤 {step.executorName}</span>}
                                {step.ownerName && <span>🏷️ {step.ownerName}</span>}
                                {step.deadline && <span style={{ color: overdue ? '#dc2626' : '#9ca3af' }}>📅 {fmtDateTime(step.deadline)}</span>}
                                {step.completedAt && <span>✅ Done {fmtDateTime(step.completedAt)} by {step.completedBy}</span>}
                              </div>
                            </div>
                            {step.status === 'in_progress' && (
                              <button onClick={() => onMarkStepDone(flow._id, step._id, auth.username, '')}
                                style={{ ...primBtn, fontSize: 11, padding: '6px 14px', flexShrink: 0 }}>✓ Mark Done</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────────

export default function FlowDashboard() {
  const { auth } = useAuth();
  const [tab, setTab] = useState('flows');           // 'flows' | 'templates'
  const [view, setView] = useState('list');          // 'list' | 'builder'
  const [flows, setFlows] = useState([]);
  const [editingFlow, setEditingFlow] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFlows = useCallback(async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/flows`);
      setFlows(res.data);
    } catch (err) {
      console.error('[FMS] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlows();
    api.get(`${process.env.REACT_APP_URL}/users`)
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUsers([]));
  }, [fetchFlows]);

  const openBuilder = (flow) => {
    setEditingFlow(flow || null);
    setView('builder');
  };

  const handleUseTemplate = (template) => {
    const flow = {
      ...emptyFlow(),
      name: '',          // user must name this instance
      description: template.description,
      templateId: template.id,
      steps: template.steps.map((s, i) => ({
        ...emptyStep(),
        ...s,
        stepNumber: i + 1,
        status: 'pending',
        how: {
          type: s.how.type,
          sopText: s.how.sopText || '',
          link: s.how.link || '',
          items: s.how.items ? [...s.how.items] : [''],
        },
      })),
    };
    openBuilder(flow);
  };

  const handleSaveFlow = async (flowData) => {
    try {
      if (editingFlow?._id) {
        await api.put(`${process.env.REACT_APP_URL}/flows/${editingFlow._id}`, { ...flowData, createdBy: auth.username });
      } else {
        await api.post(`${process.env.REACT_APP_URL}/flows`, { ...flowData, createdBy: auth.username });
      }
      await fetchFlows();
      setView('list');
      setTab('flows');
      setEditingFlow(null);
    } catch (err) {
      alert('Failed to save: ' + (err?.response?.data?.error || err.message));
    }
  };

  const handleActivate = async (flowId) => {
    try {
      await api.put(`${process.env.REACT_APP_URL}/flows/${flowId}/activate`);
      await fetchFlows();
    } catch (err) {
      alert('Failed to activate: ' + (err?.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (flowId) => {
    try {
      await api.delete(`${process.env.REACT_APP_URL}/flows/${flowId}`);
      setFlows(f => f.filter(fl => fl._id !== flowId));
    } catch (err) {
      alert('Failed to delete: ' + (err?.response?.data?.error || err.message));
    }
  };

  const handleMarkStepDone = async (flowId, stepId, completedBy, completionNotes) => {
    try {
      await api.put(`${process.env.REACT_APP_URL}/flows/${flowId}/steps/${stepId}/complete`, { completedBy, completionNotes: completionNotes || '' });
      await fetchFlows();
    } catch (err) {
      alert('Failed: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Builder view ──
  if (view === 'builder') {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
        <FlowBuilder
          flow={editingFlow}
          users={users}
          onSave={handleSaveFlow}
          onBack={() => { setView('list'); setEditingFlow(null); }}
        />
      </div>
    );
  }

  // ── List + Templates view ──
  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#1e3a5f' }}>⚡ Flow Management System</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Create, assign, and track multi-step workflows for any task or event</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setTab('templates'); }}
            style={{ ...outBtn, fontSize: 13 }}>📋 Templates</button>
          <button onClick={() => openBuilder(null)}
            style={{ ...primBtn, fontSize: 14, padding: '10px 22px' }}>+ New Flow</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
        {[{ key: 'flows', label: `⚡ All Flows (${flows.length})` }, { key: 'templates', label: '📋 Templates' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 13,
            color: tab === t.key ? '#2563eb' : '#6b7280',
            borderBottom: `3px solid ${tab === t.key ? '#2563eb' : 'transparent'}`,
            marginBottom: -2,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && tab === 'flows' ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚡</div>
          <div>Loading flows…</div>
        </div>
      ) : tab === 'templates' ? (
        <TemplatesTab onUseTemplate={handleUseTemplate} />
      ) : (
        <FlowDashboardView
          flows={flows}
          onNew={() => openBuilder(null)}
          onEdit={openBuilder}
          onActivate={handleActivate}
          onDelete={handleDelete}
          onMarkStepDone={handleMarkStepDone}
        />
      )}
    </div>
  );
}

// ─── Shared styles ─────────────────────────────────────────────────────────────

const lbl = { display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' };
const inp = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', background: '#fafafa' };
const primBtn = { padding: '9px 20px', background: 'linear-gradient(135deg, #2563eb, #1e40af)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const outBtn = { padding: '8px 16px', background: '#fff', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const dangerBtn = { padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' };
const iconBtn = { padding: '3px 8px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 12 };
const mLbl = { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 2 };
const mVal = { fontSize: 12, color: '#6b7280', fontWeight: 600 };
